class ImageGestureHandler {
  constructor(imgElement) {
    this.imgElement = imgElement;

    // Set default transform data attributes if not provided.
    this.imgElement.dataset.panX = this.imgElement.dataset.panX || 0;
    this.imgElement.dataset.panY = this.imgElement.dataset.panY || 0;
    this.imgElement.dataset.scale = this.imgElement.dataset.scale || 1;
    this.imgElement.dataset.transformOriginX =
      this.imgElement.dataset.transformOriginX || 50;
    this.imgElement.dataset.transformOriginY =
      this.imgElement.dataset.transformOriginY || 50;

    // Gesture variables for dragging.
    this.isDragging = false;
    this.hasDragged = false;
    this.startX = 0;
    this.startY = 0;
    this.currentTranslateX = 0;
    this.currentTranslateY = 0;
    this.dragThreshold = 10;
    this.ignoreClick = false;

    // Gesture variables for pinch-to-zoom.
    this.isPinching = false;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
    this.pinchStartX = 0;
    this.pinchStartY = 0;

    // Initialize our custom event listeners container.
    this.eventListeners = {};

    this.addEventListeners();

    // Expose the reset method on the instance for external access.
    this.reset = this.resetZoom.bind(this);

    // Attach the reset method to the image element so you can call img.reset()
    this.imgElement.reset = this.reset;
  }

  /**
   * Registers a callback for a given event.
   * Usage: instance.on("zoom", callback)
   */
  on(eventName, callback) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];
    }
    this.eventListeners[eventName].push(callback);
  }

  /**
   * Calls all callbacks associated with the event.
   */
  emit(eventName, data) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach((callback) => callback(data));
    }
  }

  addEventListeners() {
    this.imgElement.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.imgElement.addEventListener(
      'touchstart',
      this.onTouchStart.bind(this),
      { passive: false },
    );
    this.imgElement.addEventListener('click', this.onClick.bind(this));
  }

  clampTranslation(translateX, translateY) {
    // Use the image's parent as the container.
    const container = this.imgElement.parentElement;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const originXPercent =
      parseFloat(this.imgElement.dataset.transformOriginX) || 50;
    const originYPercent =
      parseFloat(this.imgElement.dataset.transformOriginY) || 50;
    const originX = (containerWidth * originXPercent) / 100;
    const originY = (containerHeight * originYPercent) / 100;
    const scale = parseFloat(this.imgElement.dataset.scale) || 1;

    const minTranslateX =
      containerWidth - originX - scale * (containerWidth - originX);
    const maxTranslateX = scale * originX - originX;
    const minTranslateY =
      containerHeight - originY - scale * (containerHeight - originY);
    const maxTranslateY = scale * originY - originY;

    return {
      x: Math.max(minTranslateX, Math.min(maxTranslateX, translateX)),
      y: Math.max(minTranslateY, Math.min(maxTranslateY, translateY)),
    };
  }

  resetZoom() {
    this.imgElement.style.transform = '';
    this.imgElement.style.transformOrigin = '';
    this.imgElement.classList.remove('zoomed');
    this.imgElement.dataset.panX = 0;
    this.imgElement.dataset.panY = 0;
    this.imgElement.dataset.scale = 1;
    this.imgElement.dataset.transformOriginX = 50;
    this.imgElement.dataset.transformOriginY = 50;

    // Emit a reset event providing the default values.
    this.emit('reset', {
      panX: 0,
      panY: 0,
      scale: 1,
      transformOriginX: 50,
      transformOriginY: 50,
    });
  }

  onMouseDown(e) {
    if (
      e.target.tagName !== 'IMG' ||
      !this.imgElement.classList.contains('zoomed')
    )
      return;
    e.preventDefault();
    this.isDragging = true;
    this.hasDragged = false;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.currentTranslateX = parseFloat(this.imgElement.dataset.panX) || 0;
    this.currentTranslateY = parseFloat(this.imgElement.dataset.panY) || 0;
    document.addEventListener(
      'mousemove',
      (this.onMouseMoveBound = this.onMouseMove.bind(this)),
    );
    document.addEventListener(
      'mouseup',
      (this.onMouseUpBound = this.onMouseUp.bind(this)),
    );
  }

  onMouseMove(e) {
    if (!this.isDragging) return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    if (
      !this.hasDragged &&
      (Math.abs(dx) > this.dragThreshold || Math.abs(dy) > this.dragThreshold)
    ) {
      this.hasDragged = true;
    }
    if (this.hasDragged) {
      const newTranslation = this.clampTranslation(
        this.currentTranslateX + dx,
        this.currentTranslateY + dy,
      );
      this.imgElement.style.transform = `translate(${newTranslation.x}px, ${newTranslation.y}px) scale(${this.imgElement.dataset.scale || 1})`;
    }
  }

  onMouseUp(e) {
    if (!this.isDragging) return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    const finalTranslation = this.clampTranslation(
      this.currentTranslateX + dx,
      this.currentTranslateY + dy,
    );
    this.imgElement.dataset.panX = finalTranslation.x;
    this.imgElement.dataset.panY = finalTranslation.y;
    this.isDragging = false;
    if (this.hasDragged) {
      this.ignoreClick = true;
    }
    document.removeEventListener('mousemove', this.onMouseMoveBound);
    document.removeEventListener('mouseup', this.onMouseUpBound);
  }

  onTouchStart(e) {
    if (e.touches.length === 2) {
      // Handle pinch start.
      e.preventDefault();
      this.isPinching = true;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      // Calculate midpoint of the two touches.
      this.pinchStartX = (touch1.clientX + touch2.clientX) / 2;
      this.pinchStartY = (touch1.clientY + touch2.clientY) / 2;

      // Set transform origin based on the pinch midpoint.
      const rect = this.imgElement.getBoundingClientRect();
      const xPercent = ((this.pinchStartX - rect.left) / rect.width) * 100;
      const yPercent = ((this.pinchStartY - rect.top) / rect.height) * 100;
      this.imgElement.style.transformOrigin = `${xPercent}% ${yPercent}%`;
      this.imgElement.dataset.transformOriginX = xPercent;
      this.imgElement.dataset.transformOriginY = yPercent;

      this.pinchStartDistance = this.getDistance(touch1, touch2);
      this.pinchStartScale = parseFloat(this.imgElement.dataset.scale) || 1;

      // Ensure the image is marked as zoomed.
      if (!this.imgElement.classList.contains('zoomed')) {
        this.imgElement.classList.add('zoomed');
      }

      document.addEventListener(
        'touchmove',
        (this.onTouchMoveBound = this.onTouchMove.bind(this)),
        { passive: false },
      );
      document.addEventListener(
        'touchend',
        (this.onTouchEndBound = this.onTouchEnd.bind(this)),
      );
      document.addEventListener('touchcancel', this.onTouchEndBound);
    } else if (e.touches.length === 1) {
      let touch = e.touches[0];
      if (touch.target.tagName !== 'IMG') return;
      if (this.imgElement.classList.contains('zoomed')) {
        e.preventDefault();
        this.isDragging = true;
        this.hasDragged = false;
        this.startX = touch.clientX;
        this.startY = touch.clientY;
        this.currentTranslateX = parseFloat(this.imgElement.dataset.panX) || 0;
        this.currentTranslateY = parseFloat(this.imgElement.dataset.panY) || 0;
        document.addEventListener(
          'touchmove',
          (this.onTouchMoveBound = this.onTouchMove.bind(this)),
          { passive: false },
        );
        document.addEventListener(
          'touchend',
          (this.onTouchEndBound = this.onTouchEnd.bind(this)),
        );
        document.addEventListener('touchcancel', this.onTouchEndBound);
      }
    }
  }

  onTouchMove(e) {
    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = this.getDistance(touch1, touch2);
      const scaleChange = currentDistance / this.pinchStartDistance;
      let newScale = this.pinchStartScale * scaleChange;
      newScale = Math.min(Math.max(newScale, 1), 4);

      // Panning based on pinch.
      const currentMidX = (touch1.clientX + touch2.clientX) / 2;
      const currentMidY = (touch1.clientY + touch2.clientY) / 2;
      const dx = currentMidX - this.pinchStartX;
      const dy = currentMidY - this.pinchStartY;
      const translation = this.clampTranslation(dx, dy);

      this.imgElement.dataset.scale = newScale;
      this.imgElement.dataset.panX = translation.x;
      this.imgElement.dataset.panY = translation.y;
      this.imgElement.style.transform = `translate(${translation.x}px, ${translation.y}px) scale(${newScale})`;
    } else if (this.isDragging && e.touches.length === 1) {
      let touch = e.touches[0];
      const dx = touch.clientX - this.startX;
      const dy = touch.clientY - this.startY;
      if (
        !this.hasDragged &&
        (Math.abs(dx) > this.dragThreshold || Math.abs(dy) > this.dragThreshold)
      ) {
        this.hasDragged = true;
      }
      if (this.hasDragged) {
        const newTranslation = this.clampTranslation(
          this.currentTranslateX + dx,
          this.currentTranslateY + dy,
        );
        const scale = this.imgElement.dataset.scale || 1;
        this.imgElement.style.transform = `translate(${newTranslation.x}px, ${newTranslation.y}px) scale(${scale})`;
      }
    }
  }

  onTouchEnd(e) {
    if (this.isPinching && e.touches.length < 2) {
      this.isPinching = false;
      const currentScale = parseFloat(this.imgElement.dataset.scale) || 1;
      if (currentScale < 1.1) {
        this.reset();
      }
    }
    if (this.isDragging && e.touches.length === 0) {
      if (!this.hasDragged) {
        if (this.imgElement.classList.contains('zoomed')) {
          this.reset();
        }
      } else {
        const dx = e.changedTouches[0].clientX - this.startX;
        const dy = e.changedTouches[0].clientY - this.startY;
        const finalTranslation = this.clampTranslation(
          this.currentTranslateX + dx,
          this.currentTranslateY + dy,
        );
        this.imgElement.dataset.panX = finalTranslation.x;
        this.imgElement.dataset.panY = finalTranslation.y;
      }
      this.isDragging = false;
      this.ignoreClick = true;
    }
    document.removeEventListener('touchmove', this.onTouchMoveBound);
    document.removeEventListener('touchend', this.onTouchEndBound);
    document.removeEventListener('touchcancel', this.onTouchEndBound);
  }

  getDistance(touch1, touch2) {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  onClick(e) {
    // Prevent processing clicks if the user has already dragged.
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }
    if (this.imgElement.classList.contains('zoomed')) {
      this.reset();
    } else {
      // Zoom in.
      const rect = this.imgElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const xPercent = (x / rect.width) * 100;
      const yPercent = (y / rect.height) * 100;
      this.imgElement.style.transformOrigin = `${xPercent}% ${yPercent}%`;
      this.imgElement.dataset.transformOriginX = xPercent;
      this.imgElement.dataset.transformOriginY = yPercent;
      this.imgElement.style.transform = 'scale(3)';
      this.imgElement.dataset.scale = 3;
      this.imgElement.classList.add('zoomed');
      this.imgElement.dataset.panX = 0;
      this.imgElement.dataset.panY = 0;

      // Emit a zoom event with details about the zoom.
      this.emit('zoom', {
        scale: 3,
        transformOriginX: xPercent,
        transformOriginY: yPercent,
      });
    }
  }
}

// Expose the class globally.
window.ImageGestureHandler = ImageGestureHandler;
